// ==========================================================================
// lib/contacts.ts — contacts ("who"), cards, dashboard figures (9 Oct 2026)
//   FF-042 contacts, FF-041 cards, FF-043 dashboard.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";

export type ContactKind = "business" | "person" | "friend" | "family" | "employer" | "government" | "bank" | "other";
export type Contact = {
  id: string; name: string; kind: ContactKind; person_id: string | null; match_texts: string[];
  default_category_id: string | null; default_tracker_id: string | null; default_asset_id?: string | null; notes: string | null; archived_at: string | null;
  // Contact details and birthday (025)
  phone?: string | null; email?: string | null; address?: string | null;
  birthday_day?: number | null; birthday_month?: number | null; birth_year?: number | null; show_birthday?: boolean;
};
export const CONTACT_KINDS: { key: ContactKind; label: string }[] = [
  { key: "business", label: "Business" }, { key: "friend", label: "Friend" }, { key: "person", label: "Person" }, { key: "family", label: "Family" },
  { key: "employer", label: "Employer" }, { key: "government", label: "Government" }, { key: "bank", label: "Bank" }, { key: "other", label: "Other" },
];
export type Card = { id: string; account_id: string; name: string; person_id: string | null; last4: string; archived_at: string | null };

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return (...keys: string[]) => () => { for (const k of [...keys, "audit"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

export function useContacts() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["contacts", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("contacts").select("id,name,kind,person_id,match_texts,default_category_id,default_tracker_id,default_asset_id,notes,archived_at,phone,email,address,birthday_day,birthday_month,birth_year,show_birthday").eq("household_id", hid).order("name");
      if (error) throw error;
      return data as Contact[];
    },
  });
}

/** Which contact a bank description belongs to: the longest matching text wins. */
export function contactFor(desc: string, contacts: Contact[] | undefined): Contact | undefined {
  const d = desc.toLowerCase(); let best: Contact | undefined; let len = 0;
  for (const c of contacts ?? []) {
    if (c.archived_at) continue;
    for (const m of c.match_texts) if (m && d.includes(m) && m.length > len) { best = c; len = m.length; }
  }
  return best;
}

export function useSaveContact() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, c }: { id?: string; c: Partial<Contact> }): Promise<string> => {
      if (c.match_texts) c = { ...c, match_texts: [...new Set(c.match_texts.map((m) => m.trim().toLowerCase()).filter((m) => m.length >= 3))] };
      if (id) { const { error } = await supabase.from("contacts").update(c).eq("id", id); if (error) throw error; return id; }
      const { data, error } = await supabase.from("contacts").insert({ ...c, household_id: hid }).select("id").single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: inv("contacts", "contact_totals"),
  });
}

export type ContactTotal = { contact_id: string; spent_cents: number; received_cents: number; lines: number; last_date: string | null };
export function useContactTotals(from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["contact_totals", hid, from, to], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("contact_totals", { p_household: hid, p_from: from, p_to: to });
      if (error) throw error;
      return new Map((data as ContactTotal[]).map((r) => [r.contact_id, { ...r, spent_cents: Number(r.spent_cents), received_cents: Number(r.received_cents), lines: Number(r.lines) }]));
    },
  });
}

/** Coded totals per month per budget code (spending negative, income positive). */
export function useMonthTotals(from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["split_totals", hid, "months", from, to], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("category_month_totals", { p_household: hid, p_from: from, p_to: to });
      if (error) throw error;
      return (data as { month: string; category_id: string; amount_cents: number }[]).map((r) => ({ ...r, amount_cents: Number(r.amount_cents) }));
    },
  });
}

export function useCards() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["cards", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("account_cards").select("id,account_id,name,person_id,last4,archived_at").eq("household_id", hid).order("created_at");
      if (error) throw error;
      return data as Card[];
    },
  });
}

export function useSaveCard() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, c }: { id?: string; c: Partial<Card> }) => {
      const { error } = id ? await supabase.from("account_cards").update(c).eq("id", id) : await supabase.from("account_cards").insert({ ...c, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv("cards", "txns", "unreconciled"),
  });
}

/** Budget periods: entered per week / month / quarter / year, stored as monthly. */
export const PERIODS = [
  { key: "week", label: "per week", toMonth: 52 / 12 },
  { key: "fortnight", label: "per fortnight", toMonth: 26 / 12 },
  { key: "month", label: "per month", toMonth: 1 },
  { key: "quarter", label: "per quarter", toMonth: 1 / 3 },
  { key: "year", label: "per year", toMonth: 1 / 12 },
] as const;
export type Period = (typeof PERIODS)[number]["key"];
